import { useState, useCallback, useRef, useEffect } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import logger from '../utils/logger';

/**
 * Hook for searching users in the homeserver's user directory.
 * Allows finding users by display name or user ID without needing an existing room.
 * 
 * @returns {{
 *   searchUsers: (searchTerm: string, limit?: number) => Promise<Array>,
 *   results: Array,
 *   isSearching: boolean,
 *   error: string | null,
 *   clearResults: () => void
 * }}
 */
export const useUserDirectorySearch = () => {
  const client = useMatrixClient();
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Search for users in the homeserver's user directory.
   * 
   * @param {string} searchTerm - The search term (user ID or display name)
   * @param {number} limit - Maximum number of results (default: 10)
   * @returns {Promise<Array<{user_id: string, display_name?: string, avatar_url?: string}>>}
   */
  const searchUsers = useCallback(async (searchTerm, limit = 10) => {
    if (!client) {
      const errorMsg = 'Matrix client not initialized';
      logger.error('[useUserDirectorySearch] Error:', errorMsg);
      setError(errorMsg);
      return [];
    }

    if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.trim().length === 0) {
      setResults([]);
      setError(null);
      return [];
    }

    // Cancel any in-flight search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setError(null);

    try {
      logger.debug('[useUserDirectorySearch] Searching for:', searchTerm);

      // Use Matrix SDK's searchUserDirectory method
      // https://spec.matrix.org/v1.11/client-server-api/#post_matrixclientv3user_directorysearch
      const response = await client.searchUserDirectory({
        term: searchTerm.trim(),
        limit,
      });

      const searchResults = response.results || [];
      
      logger.debug('[useUserDirectorySearch] Found results:', searchResults.length);

      // Filter out current user from results
      const myUserId = client.getUserId();
      const filteredResults = searchResults.filter(user => user.user_id !== myUserId);

      setResults(filteredResults);
      setIsSearching(false);
      return filteredResults;
    } catch (err) {
      // Don't set error if search was aborted (user typed more)
      if (err.name !== 'AbortError') {
        const errorMsg = err.message || 'Failed to search user directory';
        logger.error('[useUserDirectorySearch] Error searching:', err);
        setError(errorMsg);
        setResults([]);
      }
      setIsSearching(false);
      return [];
    }
  }, [client]);

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    searchUsers,
    results,
    isSearching,
    error,
    clearResults,
  };
};

export default useUserDirectorySearch;
